import type { FunctionComponent, ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { articleSponsorType } from "shared/data/article";

import { ExperimentTitleNames, useActiveExperiment } from "scmp-app/lib/optimizely";
import type { articleRecirculationWidgetArticle$key } from "scmp-app/queries/__generated__/articleRecirculationWidgetArticle.graphql";

import { AdSlot, Container, WidgetContainer } from "./styles";
import { ArticleFurtherReadingWidget } from "./variants/further-reading";
import { ArticleMostReadWidget } from "./variants/most-read";
import { ArticleTopicYouMayLikeWidget } from "./variants/topic-you-may-like";

type Props = {
  className?: string;
  inlineAds?: ReactNode;
  reference: articleRecirculationWidgetArticle$key;
};

export const ArticleRecirculationWidget: FunctionComponent<Props> = ({
  className,
  inlineAds,
  reference,
}) => {
  const article = useFragment(
    graphql`
      fragment articleRecirculationWidgetArticle on Article {
        sponsorType
        ...furtherReadingWidgetArticle
        ...mostReadWidgetArticle
        ...topicYouMayLikeWidgetArticle
      }
    `,
    reference,
  );

  const isSponsoredContent = useMemo(
    () => articleSponsorType.has(article.sponsorType ?? ""),
    [article.sponsorType],
  );

  const { experiment } = useActiveExperiment(ExperimentTitleNames.RecirculationWidget);

  const render = useCallback(() => {
    switch (experiment?.variation.name) {
      case "1":
        return <ArticleMostReadWidget reference={article} />;
      case "2":
        return <ArticleTopicYouMayLikeWidget reference={article} />;
      default:
        return <ArticleFurtherReadingWidget reference={article} />;
    }
  }, [article, experiment?.variation.name]);

  if (isSponsoredContent) return null;
  return (
    <Container className={className}>
      <WidgetContainer>{render()}</WidgetContainer>
      {inlineAds && <AdSlot>{inlineAds}</AdSlot>}
    </Container>
  );
};

ArticleRecirculationWidget.displayName = "ArticleRecirculationWidget";
